<template>
  <aside class="moo-cat-sidebar" :style="{ '--cat-active': activeColor, '--cat-btn': btnColor }">
    <button
      v-for="(cat, index) in categories"
      :key="cat.id || index"
      ref="items"
      class="moo-cat-sidebar__item"
      :class="{ 'is-active': isActive(cat, index) }"
      type="button"
      @click="$emit('select', { category: cat, index })"
    >
      <span class="moo-cat-sidebar__icon-wrap">
        <img
          v-if="photoFor(cat)"
          :src="photoFor(cat)"
          class="moo-cat-sidebar__photo"
          alt=""
          @error="onPhotoError(cat)"
        />
        <i
          v-else
          class="material-icons-round moo-cat-sidebar__icon"
          aria-hidden="true"
          >{{ iconNameFor(cat) }}</i
        >
      </span>
      <span class="moo-cat-sidebar__label">{{ displayLabel(cat) }}</span>
    </button>
  </aside>
</template>

<script>
import { translatedName } from "@/i18n/menuTranslate";

const ICON_MAP = {
  signature: "restaurant",
  signatures: "restaurant",
  main: "restaurant",
  mains: "restaurant",
  rice: "rice_bowl",
  noodle: "ramen_dining",
  noodles: "ramen_dining",
  soup: "soup_kitchen",
  side: "set_meal",
  sides: "set_meal",
  appetizer: "set_meal",
  appetizers: "set_meal",
  starter: "set_meal",
  starters: "set_meal",
  veg: "eco",
  vegetable: "eco",
  vegetables: "eco",
  vegan: "eco",
  vegetarian: "eco",
  drink: "local_drink",
  drinks: "local_drink",
  beverage: "local_drink",
  beverages: "local_drink",
  coffee: "local_cafe",
  tea: "emoji_food_beverage",
  dessert: "icecream",
  desserts: "icecream",
  sweet: "icecream",
  sweets: "icecream",
  cake: "cake",
  cakes: "cake",
  snack: "fastfood",
  snacks: "fastfood",
  burger: "lunch_dining",
  burgers: "lunch_dining",
  pizza: "local_pizza",
  seafood: "phishing",
  meat: "kebab_dining",
  bbq: "outdoor_grill",
  grill: "outdoor_grill",
  merch: "shopping_basket",
  merchandise: "shopping_basket",
  retail: "shopping_basket",
  combo: "restaurant_menu",
  combos: "restaurant_menu",
  set: "restaurant_menu",
  sets: "restaurant_menu",
};

const FALLBACK_ICON = "restaurant_menu";
const NO_IMAGE_SUFFIX = "/media/noimage.png";

export default {
  name: "CategorySidebar",
  props: {
    categories: { type: Array, default: () => [] },
    activeIndex: { type: Number, default: 0 },
  },
  emits: ["select"],
  data() {
    return {
      // Track per-category photo URLs that failed to load so we fall back to
      // the keyword icon instead of showing a broken image.
      photoErrors: {},
    };
  },
  computed: {
    // Merchant's Theme 2 colour for the active category highlight.
    activeColor() {
      return (
        this.$store.state.mainThemeColorTheme2 ||
        this.$store.state.color ||
        "#201a1a"
      );
    },
    // Theme colour at 50% opacity, used for the icon outline + right divider.
    btnColor() {
      const c =
        this.$store.state.mainThemeColorTheme2 ||
        this.$store.state.color ||
        "#201a1a";
      return /^#[0-9a-fA-F]{6}$/.test(c) ? c + "80" : c;
    },
  },
  watch: {
    // When the active category changes (via scroll-spy while the menu scrolls,
    // or via a tap), scroll this sidebar so the active item stays visible even
    // when the category list is taller than the screen.
    activeIndex() {
      this.$nextTick(this.scrollActiveIntoView);
    },
  },
  methods: {
    isActive(_cat, index) {
      return index === this.activeIndex;
    },
    // Scroll ONLY this sidebar's own container (never the page/menu) by the
    // minimum amount needed to bring the active item fully into view.
    scrollActiveIntoView() {
      const container = this.$el;
      const items = this.$refs.items;
      const item = items && items[this.activeIndex];
      if (!container || !item) return;

      const cRect = container.getBoundingClientRect();
      const iRect = item.getBoundingClientRect();
      const PAD = 8;

      // The sidebar's scroll box can extend below the fixed bottom nav, so its
      // geometric bottom is occluded. Clip the effective visible bottom to the
      // nav's top edge, otherwise the last icons stop half-hidden behind it.
      const nav = document.querySelector(".moo-bottom-nav");
      const navTop = nav ? nav.getBoundingClientRect().top : Infinity;
      const visibleBottom = Math.min(cRect.bottom, navTop);

      let delta = 0;
      if (iRect.top < cRect.top + PAD) {
        delta = iRect.top - cRect.top - PAD;
      } else if (iRect.bottom > visibleBottom - PAD) {
        delta = iRect.bottom - visibleBottom + PAD;
      }
      if (delta === 0) return;

      const top = container.scrollTop + delta;
      try {
        container.scrollTo({ top, behavior: "smooth" });
      } catch (e) {
        container.scrollTop = top;
      }
    },
    labelFor(cat) {
      return (
        cat.second_name ||
        cat.category_name ||
        cat.name ||
        cat.title ||
        ""
      );
    },
    // Translated for display only — labelFor() keeps returning the source name
    // so the icon lookup below still matches its English keywords.
    displayLabel(cat) {
      return translatedName(this.labelFor(cat));
    },
    photoFor(cat) {
      const url = cat && cat.img_version;
      if (!url || typeof url !== "string") return null;
      if (url.endsWith(NO_IMAGE_SUFFIX)) return null;
      const key = cat.id != null ? cat.id : url;
      if (this.photoErrors[key]) return null;
      return url;
    },
    onPhotoError(cat) {
      const key = cat && cat.id != null ? cat.id : cat && cat.img_version;
      if (key == null) return;
      this.photoErrors = { ...this.photoErrors, [key]: true };
    },
    iconNameFor(cat) {
      if (cat && cat.icon_name) return cat.icon_name;
      const raw = (this.labelFor(cat) || "").toLowerCase().trim();
      if (!raw) return FALLBACK_ICON;
      const tokens = raw.split(/[\s_\-/&]+/).filter(Boolean);
      for (const t of tokens) {
        if (ICON_MAP[t]) return ICON_MAP[t];
      }
      for (const t of tokens) {
        for (const k of Object.keys(ICON_MAP)) {
          if (t.includes(k)) return ICON_MAP[k];
        }
      }
      return FALLBACK_ICON;
    },
  },
};
</script>

<style scoped lang="scss">
.moo-cat-sidebar {
  flex: 0 0 80px;
  align-self: flex-start;
  position: sticky;
  top: var(--cat-top, 0);
  height: var(--cat-height, 100%);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  padding: 1rem 0 8rem;
  background: var(--theme-30, transparent);
  border-right: 1px solid var(--moo-outline, var(--color));
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.moo-cat-sidebar__item {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
  color: #9a9a9a;
  width: 64px;
  transition: transform 0.15s ease;

  &:active {
    transform: scale(0.92);
  }
}

.moo-cat-sidebar__icon-wrap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.04);
  border: 3px solid transparent;
  color: #6b6b6b;
  overflow: hidden;
  transition: background 0.18s ease, color 0.18s ease, transform 0.18s ease;
}

.moo-cat-sidebar__icon {
  font-size: 24px;
}

.moo-cat-sidebar__photo {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.moo-cat-sidebar__label {
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #9a9a9a;
  text-align: center;
  line-height: 1.2;
  word-break: break-word;
}

.moo-cat-sidebar__item.is-active {
  color: var(--cat-active);

  .moo-cat-sidebar__icon-wrap {
    background: transparent;
    border-color: var(--cat-active);
    color: var(--cat-active);
  }

  .moo-cat-sidebar__label {
    color: var(--cat-active);
    font-weight: 700;
  }
}

.moo-cat-sidebar__item.is-active .moo-cat-sidebar__photo {
  // keep photo visible even when the wrap is theme-filled
  mix-blend-mode: normal;
}
</style>
